import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, AlertTriangle, Clock, Shield, Building2, CreditCard, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const BookingConfirmationPage = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [booking, setBooking] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, pending, success, failed, expired
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer'); // bank_transfer, online (disabled)
  const [copiedIban, setCopiedIban] = useState(false);
  
  // Bank Transfer Details
  const bankDetails = {
    accountHolder: "MUSTAFA BUĞRA ŞANLI",
    bank: "GARANTİ BANKASI",
    iban: "TR24 0006 2000 7090 0006 2953 42"
  };

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  useEffect(() => {
    // Countdown Timer logic
    if (booking?.hold_expires_at && status === 'pending') {
      const interval = setInterval(() => {
        const now = new Date();
        const expires = new Date(booking.hold_expires_at);
        const diff = expires - now;

        if (diff <= 0) {
          clearInterval(interval);
          setStatus('expired');
          setTimeLeft(0);
        } else {
          setTimeLeft(diff);
        }
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [booking, status]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`, { withCredentials: true });
      const data = response.data;
      setBooking(data);
      
      if (data.payment_status === 'CONFIRMED') {
        setStatus('success');
      } else if (data.payment_status === 'EXPIRED') {
        setStatus('expired');
      } else if (data.payment_status === 'PENDING_PAYMENT') {
        setStatus('pending');
      } else {
        setStatus('failed');
      }
    } catch (error) {
      console.error('Fetch booking error:', error);
      toast.error('Rezervasyon bilgileri alınamadı');
      setStatus('error');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopiedIban(true);
    toast.success('İban kopyalandı!');
    setTimeout(() => setCopiedIban(false), 2000);
  };

  const handleBankTransferConfirm = async () => {
    try {
      // Update booking to indicate bank transfer payment initiated
      await axios.post(`${API}/bookings/${bookingId}/bank-transfer-initiated`, {}, { withCredentials: true });
      toast.success('Havale bilgileri kaydedildi. Ödeme onayı bekleniyor.');
      navigate('/hesabim');
    } catch (error) {
      console.error('Bank transfer confirm error:', error);
      toast.error('İşlem kaydedilemedi');
    }
  };

  const formatTime = (ms) => {
    if (ms <= 0) return "00:00";
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  if (status === 'loading') {
    return <div className="flex justify-center items-center h-screen">Yükleniyor...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        
        {/* SUCCESS STATE */}
        {status === 'success' && (
          <Card className="border-emerald-500 bg-emerald-50">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-emerald-800 mb-2">Ödeme Başarılı!</h2>
              <p className="text-emerald-700 mb-6">Rezervasyonunuz onaylanmıştır.</p>
              <div className="bg-white p-4 rounded-lg shadow-sm max-w-sm mx-auto mb-6">
                <p className="text-sm text-slate-500">Rezervasyon Kodu</p>
                <p className="text-xl font-mono font-bold text-slate-900">{booking?.booking_code}</p>
              </div>
              <Button onClick={() => navigate('/hesabim')} className="bg-emerald-600 hover:bg-emerald-700">
                Rezervasyonlarımı Gör
              </Button>
            </CardContent>
          </Card>
        )}

        {/* EXPIRED STATE */}
        {status === 'expired' && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6 text-center">
              <Clock className="w-16 h-16 text-red-400 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-red-800 mb-2">Süre Doldu</h2>
              <p className="text-red-700 mb-6">
                Ödeme için ayrılan 10 dakikalık süre dolduğu için bu slot serbest bırakıldı.
              </p>
              <Button onClick={() => navigate('/')} variant="outline" className="border-red-200 text-red-700 hover:bg-red-100">
                Tekrar Arama Yap
              </Button>
            </CardContent>
          </Card>
        )}

        {/* PENDING / PAYMENT STATE */}
        {status === 'pending' && booking && (
          <div className="space-y-6">
            <div className="flex items-center justify-between bg-amber-50 p-4 rounded-lg border border-amber-200">
              <div className="flex items-center gap-2 text-amber-800">
                <Clock className="w-5 h-5" />
                <span className="font-semibold">Kalan Süre:</span>
              </div>
              <span className="text-xl font-mono font-bold text-amber-700">
                {formatTime(timeLeft)}
              </span>
            </div>

            {/* Reservation Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Rezervasyon Özeti</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-slate-500">Tesis</span>
                  <span className="font-medium">{booking.facility_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Tarih</span>
                  <span className="font-medium">{booking.date}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Saat</span>
                  <span className="font-medium">{booking.start_time} - {booking.end_time}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Rezervasyon Kodu</span>
                  <span className="font-mono font-medium">{booking.booking_code}</span>
                </div>
                <hr />
                <div className="flex justify-between text-lg">
                  <span className="font-bold text-slate-700">Toplam Tutar</span>
                  <span className="font-bold text-emerald-600">{booking.brut_tutar} ₺</span>
                </div>
              </CardContent>
            </Card>

            {/* Payment Method Selection */}
            <Card>
              <CardHeader>
                <CardTitle>Ödeme Yöntemi Seçin</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Bank Transfer Option */}
                <div 
                  onClick={() => setPaymentMethod('bank_transfer')}
                  className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                    paymentMethod === 'bank_transfer' 
                      ? 'border-emerald-500 bg-emerald-50' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Building2 className={`w-6 h-6 ${paymentMethod === 'bank_transfer' ? 'text-emerald-600' : 'text-slate-400'}`} />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">Havale / EFT</h3>
                      <p className="text-sm text-slate-500">Banka havalesi ile ödeme yapın</p>
                    </div>
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === 'bank_transfer' 
                        ? 'border-emerald-500 bg-emerald-500' 
                        : 'border-slate-300'
                    }`}>
                      {paymentMethod === 'bank_transfer' && <Check className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </div>

                {/* Online Payment Option - Coming Soon */}
                <div className="p-4 border-2 border-slate-200 rounded-lg opacity-60 cursor-not-allowed relative">
                  <div className="flex items-center gap-3">
                    <CreditCard className="w-6 h-6 text-slate-400" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-slate-900">Online Ödeme</h3>
                      <p className="text-sm text-slate-500">Kredi/Banka kartı ile ödeme</p>
                    </div>
                    <span className="absolute top-2 right-2 bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded">
                      Çok Yakında
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Bank Transfer Details */}
            {paymentMethod === 'bank_transfer' && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardHeader className="bg-emerald-100 border-b border-emerald-200">
                  <CardTitle className="flex items-center gap-2 text-emerald-900">
                    <Building2 className="w-5 h-5" />
                    Havale Bilgileri
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                  <div className="bg-white p-4 rounded-lg space-y-3">
                    <div>
                      <label className="text-xs text-slate-500 font-medium">Hesap Sahibi</label>
                      <p className="text-sm font-semibold text-slate-900">{bankDetails.accountHolder}</p>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500 font-medium">Banka</label>
                      <p className="text-sm font-semibold text-slate-900">{bankDetails.bank}</p>
                    </div>
                    
                    <div>
                      <label className="text-xs text-slate-500 font-medium">İBAN</label>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-sm font-mono font-bold text-slate-900 flex-1">{bankDetails.iban}</p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(bankDetails.iban)}
                          className="gap-2"
                        >
                          {copiedIban ? (
                            <>
                              <Check className="w-4 h-4 text-emerald-600" />
                              <span className="text-emerald-600">Kopyalandı</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              Kopyala
                            </>
                          )}
                        </Button>
                      </div>
                    </div>

                    <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
                      <p className="text-xs text-amber-800">
                        <strong>Önemli:</strong> Havale açıklamasına mutlaka rezervasyon kodunuzu yazınız:
                      </p>
                      <p className="text-sm font-mono font-bold text-amber-900 mt-1">{booking.booking_code}</p>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">📋 Havale Talimatları</h4>
                    <ol className="text-xs text-blue-800 space-y-1 list-decimal list-inside">
                      <li>Yukarıdaki İBAN numarasına <strong>{booking.brut_tutar} ₺</strong> tutarında havale yapın</li>
                      <li>Açıklama kısmına rezervasyon kodunuzu (<strong>{booking.booking_code}</strong>) yazın</li>
                      <li>Havale dekontunu WhatsApp ile iletebilirsiniz</li>
                      <li>Ödemeniz onaylandıktan sonra rezervasyonunuz kesinleşecektir</li>
                    </ol>
                  </div>

                  <Button 
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                    onClick={handleBankTransferConfirm}
                  >
                    Havale Yaptım, Onay Bekliyorum
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingConfirmationPage;
